import React from 'react';


export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
      {/* Privacy Policy Content */}
      <main className="pt-16 pb-20 bg-[#111010]">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-[#363232] border border-[#504A4A] rounded-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#EBFFEE]">Privacy Policy</h1>
            <p className="text-center text-[#A9A3A3] mb-8">RollOps LLC</p>
            <p className="text-center text-[#A9A3A3] mb-12">
              <strong>Effective Date:</strong> August 22, 2025
            </p>

            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-[#A9A3A3] mb-8 leading-relaxed">
                This Privacy Policy (&quot;Policy&quot;) sets forth the privacy practices of RollOps LLC, a Wyoming limited
                liability company (&quot;RollOps,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), with respect to the collection, use, storage,
                disclosure, and protection of personal and non-personal information obtained through our mobile
                application, website, and associated services (collectively, the &quot;Service&quot;). By accessing or using the
                Service, you (&quot;you&quot; or &quot;User&quot;) acknowledge and agree to the terms and conditions of this Policy.
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">I. INFORMATION WE COLLECT</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  We collect information that identifies, relates to, describes, or is reasonably capable of being
                  associated with a particular User (&quot;Personal Information&quot;), as well as information that is not
                  associated with a specific individual (&quot;Non-Personal Information&quot;).
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">A. Personal Information</h3>
                <p className="text-[#A9A3A3] mb-3">Personal Information may include, but is not limited to:</p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-1">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Telephone number</li>
                  <li>Date of birth (used for age-appropriate class filtering and safety compliance)</li>
                  <li>Payment and billing data (processed by our PCI-compliant provider; we do not store full card numbers)</li>
                  <li>Login credentials, including usernames and passwords</li>
                  <li>Location information (employees only), when permission is granted, used solely to validate geofence entry/exit for clock-in/out</li>
                  <li>
                    User-generated content, including training journal entries and communication within the platform
                  </li>
                </ul>

                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  <strong>Date of Birth Usage:</strong> We collect your date of birth to ensure age-appropriate class filtering, 
                  comply with safety regulations for combat sports, and provide appropriate content and features based on your age group. 
                  This information helps gyms organize classes by age brackets and ensures compliance with local regulations regarding 
                  youth participation in martial arts activities.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">B. Non-Personal Information</h3>
                <p className="text-[#A9A3A3] mb-3">
                  Non-Personal Information is collected through automated means and may include:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Device type and identifiers</li>
                  <li>Referring and exit URLs</li>
                  <li>Usage data and behavioral metrics (e.g., page views, click patterns, session durations)</li>
                </ul>
                <p className="text-[#A9A3A3] leading-relaxed">
                  We use cookies and similar tracking technologies (e.g., pixels, SDKs) to collect Non-Personal
                  Information. Users may disable cookies through their browser settings, but this may limit
                  functionality of the Service.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">
                  II. LEGAL BASES FOR PROCESSING (GDPR COMPLIANCE)
                </h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  If you are located in the European Economic Area (EEA), we process your Personal Information only
                  where we have legal bases for doing so under applicable EU laws. This means we collect and use your
                  Personal Information only when:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] space-y-2">
                  <li>We need it to provide you with our services and to fulfill our contractual obligations;</li>
                  <li>
                    It is justified because of a legitimate interest, such as for research and development, to market
                    and promote our services, and to protect our legal rights;
                  </li>
                  <li>You have given consent for a specific use; or</li>
                  <li>We need to process your data to comply with a legal obligation.</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">III. USE OF INFORMATION</h2>
                <p className="text-[#A9A3A3] mb-4">
                  We may use your Personal and Non-Personal Information for the following purposes:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] space-y-2">
                  <li>To provide, operate, and maintain the Service</li>
                  <li>To manage User accounts and profiles</li>
                  <li>To facilitate transactions and process payments</li>
                  <li>To communicate with Users, including sending administrative and marketing messages</li>
                  <li>To improve the functionality, performance, and security of the Service</li>
                  <li>To comply with applicable legal obligations</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">IV. DISCLOSURE OF INFORMATION</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  We do not sell or rent Personal Information to third parties. We may share your Personal Information
                  under the following limited circumstances:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] space-y-3">
                  <li>
                    <strong className="text-[#EBFFEE]">Service Providers:</strong> With vendors, consultants, and
                    third-party service providers who perform services on our behalf under binding confidentiality and
                    data protection terms.
                  </li>
                  <li>
                    <strong className="text-[#EBFFEE]">Legal Compliance:</strong> To comply with applicable laws,
                    regulations, or legal process or to respond to valid legal requests (e.g., subpoenas, court orders).
                  </li>
                  <li>
                    <strong className="text-[#EBFFEE]">Business Transfers:</strong> In connection with, or during
                    negotiations of, any merger, acquisition, sale of assets, financing, or transfer of all or a portion
                    of our business to another entity.
                  </li>
                  <li>
                    <strong className="text-[#EBFFEE]">Protection of Rights:</strong> To protect and defend our rights,
                    property, or safety, or those of our Users or third parties.
                  </li>
                </ul>
                <p className="text-[#A9A3A3] mt-4 leading-relaxed">
                  We reserve the right to use and disclose aggregated, anonymized data for analytics, research, or
                  marketing purposes.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">
                  V. YOUR PRIVACY RIGHTS (INCLUDING CCPA/CPRA & GDPR)
                </h2>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">A. California Privacy Rights</h3>
                <p className="text-[#A9A3A3] mb-3 leading-relaxed">
                  If you are a California resident, under the California Consumer Privacy Act (CCPA) and California
                  Privacy Rights Act (CPRA), you have the right to:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>Know what Personal Information is collected, disclosed, and sold;</li>
                  <li>Request access to your Personal Information;</li>
                  <li>Request deletion of your Personal Information;</li>
                  <li>Opt out of the sale or sharing of Personal Information;</li>
                  <li>Correct inaccurate Personal Information;</li>
                  <li>Limit the use of sensitive Personal Information.</li>
                </ul>
                <p className="text-[#A9A3A3] mb-6 leading-relaxed">
                  We do not sell your Personal Information. To exercise your California rights, contact us at{" "}
                  <a href="mailto:privacy@rollops.pro" className="text-rollops-accent hover:underline">
                    privacy@rollops.pro
                  </a>
                  .
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">B. European Union / EEA Data Subject Rights</h3>
                <p className="text-[#A9A3A3] mb-3">If you are located in the EEA or UK, you have the right to:</p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>Access, correct, or delete your Personal Information;</li>
                  <li>Object to processing or request restrictions;</li>
                  <li>Withdraw consent at any time;</li>
                  <li>Request data portability;</li>
                  <li>Lodge a complaint with your local data protection authority.</li>
                </ul>
                <p className="text-[#A9A3A3] leading-relaxed">
                  To exercise any of these rights, contact us at{" "}
                  <a href="mailto:privacy@rollops.pro" className="text-rollops-accent hover:underline">
                    privacy@rollops.pro
                  </a>
                  .
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">VI. DATA RETENTION</h2>
                <p className="text-[#A9A3A3] leading-relaxed">
                  We retain Personal Information for as long as necessary to provide the Service, comply with legal
                  obligations, resolve disputes, and enforce agreements. When information is no longer required, we
                  securely delete or anonymize it. Limited records required for legal compliance, liability protection,
                  or business operations (including billing history, attendance logs, and signed waivers) may be retained
                  even after account deletion, consistent with our obligations and the RollOps app EULA.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">VII. CHILDREN&apos;S PRIVACY</h2>
                <p className="text-[#A9A3A3] leading-relaxed">
                  The Service is not intended for use by individuals under the age of 13. We do not knowingly collect or
                  solicit Personal Information from individuals under the age of 13. In the event we become aware that
                  such information has been collected, we will take steps to delete it. Parents or legal guardians may
                  contact us at{" "}
                  <a href="mailto:privacy@rollops.pro" className="text-rollops-accent hover:underline">
                    privacy@rollops.pro
                  </a>{" "}
                  to request deletion.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">VIII. LOCATION PERMISSIONS & BACKGROUND GEOFENCING</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps uses device location for employee clock-in/out with geofencing. When enabled, the app may
                  request Foreground and Background Location permissions so we can automatically clock employees out
                  when they leave a work location, even if the app isn&apos;t open.
                </p>
                <p className="text-[#A9A3A3] leading-relaxed">
                  We do <strong>not</strong> track users across other companies&apos; apps or websites, and we do not use
                  advertising identifiers. Precise coordinates are <strong>not stored</strong> in our database; we only
                  persist the clock-in/clock-out event and related location identifier.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">IX. DATA SECURITY</h2>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  We implement reasonable administrative, technical, and physical security measures to protect your
                  information against unauthorized access, alteration, disclosure, or destruction, including:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>Encryption at rest and in transit</li>
                  <li>Secure server environments</li>
                  <li>Access controls and authentication protocols</li>
                </ul>
                <p className="text-[#A9A3A3] leading-relaxed">
                  Despite these measures, no method of transmission over the internet or electronic storage is
                  completely secure. You use the Service at your own risk.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">X. USER CONTROLS & OPT-OUT CHOICES</h2>
                <p className="text-[#A9A3A3] mb-3">You may access, update, or delete your Personal Information by:</p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-4 space-y-1">
                  <li>Logging into your account settings</li>
                  <li>
                    Contacting us directly at{" "}
                    <a href="mailto:privacy@rollops.pro" className="text-rollops-accent hover:underline">
                      privacy@rollops.pro
                    </a>
                  </li>
                </ul>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  You may opt out of marketing communications at any time by clicking the unsubscribe link included in
                  our emails. Note: You will still receive service-related communications.
                </p>
                <p className="text-[#A9A3A3] mb-2">
                  You may also control cookie use through browser settings or opt out of behavioral advertising via
                  tools such as:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] space-y-1">
                  <li>
                    <a
                      href="http://optout.networkadvertising.org"
                      className="text-rollops-accent hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      http://optout.networkadvertising.org
                    </a>
                  </li>
                  <li>
                    <a
                      href="http://optout.aboutads.info"
                      className="text-rollops-accent hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      http://optout.aboutads.info
                    </a>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">XI. THIRD-PARTY SERVICES AND LINKS</h2>
                <p className="text-[#A9A3A3] leading-relaxed">
                  Our Service may contain links to third-party websites, services, or applications. This Policy does not
                  govern the data practices of such third parties. We encourage you to review the privacy policies of
                  those third parties before providing any information.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">XII. INTERNATIONAL DATA TRANSFERS</h2>
                <p className="text-[#A9A3A3] leading-relaxed">
                  If you are accessing our Service from outside the United States, be aware that your information may be
                  transferred to, stored, and processed in the United States. By using the Service, you consent to the
                  transfer of your information to the U.S. and other jurisdictions where privacy laws may not be as
                  protective.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">XIII. CHANGES TO THIS PRIVACY POLICY</h2>
                <p className="text-[#A9A3A3] leading-relaxed">
                  We reserve the right to amend or update this Policy at any time. Material changes will be communicated
                  via email or in-app notification no less than thirty (30) days before taking effect. Continued use of
                  the Service after such notice constitutes your acceptance of the changes.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">XIV. CONTACT INFORMATION</h2>
                <p className="text-[#A9A3A3] mb-4">
                  If you have questions or concerns regarding this Policy or our privacy practices, please contact:
                </p>
                <div className="bg-[#221F1F] border border-[#504A4A] p-6 rounded-lg">
                  <p className="text-[#EBFFEE] font-semibold mb-2">RollOps LLC</p>
                  <p className="text-[#A9A3A3] mb-2">
                    Email:{" "}
                    <a href="mailto:privacy@rollops.pro" className="text-rollops-accent hover:underline">
                      privacy@rollops.pro
                    </a>
                  </p>
                  <p className="text-[#A9A3A3]">Mailing Address: 1908 Thomes Ave STE 12408 Cheyenne, WY 82001</p>
                </div>
              </section>

              <div className="text-center pt-8 border-t border-[#504A4A]">
                <p className="text-[#A9A3A3]">This Privacy Policy was last updated on August 22, 2025.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  )
}
